<template>
  <div class="card">
    <div class="card-container">
      <div v-if="$slots.header" class="card-header">
        <slot name="header" />
      </div>

      <div v-if="cardTitle || subtitleTag" class="card-title">
        <component :is="subtitleTag" v-if="cardSubtitle" class="card-title-secondary">
          {{ cardSubtitle }}
        </component>

        <component
          :is="titleTag"
          v-if="cardTitle"
          :class="['card-title-primary', titleVariantClass]"
        >
          {{ cardTitle }}
        </component>
      </div>

      <div class="card-body">
        <slot />
      </div>

      <div v-if="$slots.footer" class="card-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

type TitleFontSize = 'size20-18' | 'size20-20'

export default defineComponent({
  props: {
    cardSubtitle: {
      type: String,
      default: '',
    },
    cardTitle: {
      type: String,
      default: '',
    },
    subtitleTag: {
      type: String,
      default: 'h2',
    },
    titleTag: {
      type: String,
      default: 'h1',
    },
    titleSize: {
      type: String as () => TitleFontSize,
      default: 'size20-18',
      validator(value: string) {
        return ['size20-18', 'size20-20'].includes(value);
      },
    },
  },
  setup(props) {
    const titleVariantList: Record<TitleFontSize, string> = {
      'size20-18': 'type-a',
      'size20-20': 'type-b',
    };
    const titleVariantClass = computed(() => titleVariantList[props.titleSize]);

    return {
      titleVariantClass,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  background-color: #FFF;
  border-radius: 8px;
  box-shadow: 0 4px 8px 0 rgba(#000, .16);

  .card-container {
    padding: 1rem;
  }
}

.card-header {
  margin-bottom: .5rem;
}

.card-title {
  margin-bottom: 1rem;

  .card-title-primary {
    font-weight: 700;
    font-size: 1.25rem;
    margin: 0;

    &.type-a {
      @include responsive($tablet) {
        font-size: 1.125rem;
      }
    }
  }

  .card-title-secondary {
    color: var(--neutral-64);
    font-weight: 400;
    font-size: .875rem;
    margin: 0;

    @include responsive($tablet) {
      font-size: 1rem;
    }
  }
}

.card-body {
  line-height: 1.4;
}

.card-footer {
  margin-top: 2rem;
}
</style>
